import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'

type TourStep = {
  target: string
  title: string
  body: string
}

type Props = {
  isOpen: boolean
  onClose: (completed: boolean) => void
}

const steps: TourStep[] = [
  {
    target: 'add-job',
    title: 'Add jobs fast',
    body: 'Paste a job posting URL and JobTrackr extracts the role, company, salary, requirements, and stages for review.',
  },
  {
    target: 'dashboard-stats',
    title: 'Read your job search at a glance',
    body: 'Stats summarize activity, progress, salary context, and Pro-only analytics as your pipeline grows.',
  },
  {
    target: 'applications-list',
    title: 'Work your application pipeline',
    body: 'Cards and tables let you update status, stages, notes, favorites, salaries, and follow-up reminders.',
  },
  {
    target: 'follow-ups',
    title: 'Stay ahead of reminders',
    body: 'Follow-ups keep interviews, emails, and deadlines visible so opportunities do not quietly drift away.',
  },
  {
    target: 'nav-resume',
    title: 'Improve your resume',
    body: 'Resume Analysis checks ATS fit and gives you practical suggestions before you send another application.',
  },
  {
    target: 'nav-analytics',
    title: 'Spot bottlenecks',
    body: 'Analytics shows where applications stall, which stages convert, and where to focus your next push.',
  },
]

const getRect = (target: string) => {
  const element = getTargetElement(target)
  if (!element) return null
  return element.getBoundingClientRect()
}

const getTargetElement = (target: string) => {
  const elements = Array.from(document.querySelectorAll(`[data-tour="${target}"]`))
  return elements.find((candidate): candidate is HTMLElement => {
    if (!(candidate instanceof HTMLElement)) return false
    const rect = candidate.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }) ?? null
}

export const ProductTour = ({ isOpen, onClose }: Props) => {
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const step = steps[index]

  const updateRect = () => {
    if (!step) return
    const nextRect = getRect(step.target)
    setRect(nextRect)
  }

  useEffect(() => {
    if (!isOpen) return
    setIndex(0)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !step) return
    const element = getTargetElement(step.target)
    if (element) {
      element.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    }
    const timeout = window.setTimeout(updateRect, 220)
    window.addEventListener('resize', updateRect)
    window.addEventListener('scroll', updateRect, true)
    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener('resize', updateRect)
      window.removeEventListener('scroll', updateRect, true)
    }
  }, [isOpen, step?.target])

  const cardPosition = useMemo(() => {
    if (!rect) {
      return {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }
    }

    const width = Math.min(360, window.innerWidth - 32)
    const gap = 16
    const viewportPadding = 16
    const canPlaceRight = rect.right + width + gap < window.innerWidth
    const canPlaceLeft = rect.left - width - gap > 0
    const left = canPlaceRight
      ? rect.right + gap
      : canPlaceLeft
        ? rect.left - width - gap
        : Math.min(Math.max(viewportPadding, rect.left), window.innerWidth - width - viewportPadding)
    const top = Math.min(Math.max(viewportPadding, rect.top), window.innerHeight - 260)

    return {
      left,
      top,
      transform: 'none',
    }
  }, [rect])

  if (!isOpen || !step) return null

  const isLast = index === steps.length - 1

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none">
      <div className="absolute inset-0 bg-app-surface/35" />

      {rect && (
        <div
          className="absolute rounded-2xl border-2 border-sky-400 dark:border-app-accent dark:shadow-[0_0_0_9999px_rgba(0,0,0,0.56)] shadow-[0_0_0_9999px_rgba(2,6,23,0.46),0_0_24px_rgba(14,165,233,0.45)] transition-all duration-200"
          style={{
            left: Math.max(8, rect.left - 8),
            top: Math.max(8, rect.top - 8),
            width: Math.min(window.innerWidth - 16, rect.width + 16),
            height: rect.height + 16,
          }}
        />
      )}

      <div
        className="pointer-events-auto absolute w-[calc(100vw-2rem)] max-w-[360px] rounded-2xl border border-app-line bg-app-surface p-5 shadow-2xl"
        style={cardPosition}
      >
        <button
          type="button"
          onClick={() => onClose(false)}
          className="absolute right-3 top-3 rounded-full p-1.5 text-app-muted transition hover:bg-app-subtle hover:text-app-ink"
          aria-label="Skip product tour"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600 dark:text-app-accent">
          Step {index + 1} of {steps.length}
        </p>
        <h2 className="mt-2 pr-8 text-lg font-bold text-app-ink font-display">{step.title}</h2>
        <p className="mt-3 text-sm leading-6 text-app-muted">{step.body}</p>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setIndex((current) => Math.max(0, current - 1))}
            disabled={index === 0}
            className="inline-flex items-center gap-2 border border-app-line px-3 py-2 text-sm font-semibold text-app-ink transition hover:bg-app-subtle disabled:opacity-40 rounded-lg"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <button
            type="button"
            onClick={() => {
              if (isLast) {
                onClose(true)
              } else {
                setIndex((current) => current + 1)
              }
            }}
            className="inline-flex items-center gap-2 bg-app-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-app-primary-hover rounded-lg"
          >
            {isLast ? 'Finish tour' : 'Next'}
            {!isLast && <ArrowRightIcon className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}
